import {
  Platform,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MarisaServiceLogoSVG from '~/assets/svgs/marisaServicesLogo.svg';
import MarisaSVG from '~/assets/svgs/marisa.svg';
import { Button, Text } from '~/components';
import ErrorSVG from '~/assets/svgs/error.svg';
import CloseErrorSVG from '~/assets/svgs/closeError.svg';
import CloseSVG from '~/assets/svgs/close.svg';
import useCredsystemAuthStore from '~/credsystem/stores/credsystemAuth';
import CodeDigits from '~/credsystem/components/CodeDigits';
import { useRoute, type RouteProp } from '@react-navigation/native';
import AttemptsErrorModal from './components/AttemptsErrorModal';
import type { RootStackParamList } from '~/navigation/types';
import useAppNavigation from '~/hooks/useAppNavigation';

const SECONDS = 60;

const OTPValidation = () => {
  const { navigate, goBack } = useAppNavigation();
  const _intervalId = useRef<NodeJS.Timeout>();

  const { params } =
    useRoute<RouteProp<RootStackParamList, 'Credsystem_OTPValidation'>>();

  const [error, setError] = useState<string>('');
  const [seconds, setSeconds] = useState<number>(SECONDS);
  const [wrongCode, setWrongCode] = useState<boolean>(false);
  const [attempts, setAttempts] = useState<number>(0);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');

  const createTimer = useCallback(() => {
    if (_intervalId && _intervalId.current) {
      clearInterval(_intervalId.current);
    }

    setSeconds(SECONDS);
    _intervalId.current = setInterval(
      () => setSeconds((state) => state - 1),
      1000
    );
  }, []);

  const handleSendCode = useCallback(async () => {
    setButtonLoading(true);
    if (attempts + 1 === 6) {
      setButtonLoading(false);
    } else {
      try {
        await useCredsystemAuthStore.getState().sendSms();

        setAttempts((state) => ++state);
        createTimer();
      } catch (err) {
        console.log(err);
        setError(
          'Não foi possível reenviar o código. Tente novamente mais tarde.'
        );
      } finally {
        setButtonLoading(false);
      }
    }
  }, [attempts, createTimer]);

  const clearAttempts = useCallback(() => {
    setAttempts(0);
    setSeconds(SECONDS);
    createTimer();
  }, [createTimer]);

  const RenderLinkButton = useCallback(() => {
    if (seconds > 0) {
      return (
        <Text className="text-sm text-center text-light-300 mt-6">
          Reenviar código em: {` `}
          {seconds} segundos
        </Text>
      );
    }

    return (
      <View className="justify-center items-center mt-6">
        <TouchableOpacity onPress={handleSendCode}>
          <Text className="text-sm font-bold text-center text-secondary-600 ">
            Reenviar código
          </Text>
        </TouchableOpacity>
      </View>
    );
  }, [handleSendCode, seconds]);

  const handleValidate = useCallback(async () => {
    setButtonLoading(true);
    try {
      const validaPinCodeResponse = await useCredsystemAuthStore
        .getState()
        .validatePinCode({
          document: useCredsystemAuthStore.getState().document,
          password: useCredsystemAuthStore.getState().password,
          pinCode: code,
        });

      if (validaPinCodeResponse.error) {
        throw new Error(validaPinCodeResponse.message);
      }

      setButtonLoading(false);

      navigate('Credsystem_Dashboard');
    } catch (err) {
      if (err instanceof Error)
        setError(err.message || 'Opa! Seu código parece estar incorreto.');
      setWrongCode(true);

      setButtonLoading(false);
    }
  }, [code, navigate]);

  useEffect(() => {
    if (_intervalId && _intervalId.current && seconds === 0) {
      clearInterval(_intervalId.current);
    }
  }, [seconds]);

  useEffect(() => {
    createTimer();
  }, [createTimer]);

  return (
    <>
      <AttemptsErrorModal visible={attempts >= 5} onDismiss={clearAttempts} />
      <SafeAreaView className="bg-white h-full">
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            flexDirection: 'row',
          }}
          className="h-full"
        >
          <View className="flex-1 bg-primary-600 items-center justify-center hidden lg:flex">
            <MarisaServiceLogoSVG />
          </View>

          <View className="flex-1 bg-white items-center justify-start px-10 pt-24 lg:justify-center lg:pt-16">
            <MarisaSVG />

            <View className="w-full">
              <Text
                className={`text-[22px] mt-8 mb-14 ${
                  Platform.OS === 'web' ? ' md:text-center' : 'text-left'
                }`}
              >
                Para a segurança dessa operação precisamos fazer uma confirmação
                via telefone. Insira o código enviado para o número{' '}
                <Text className="text-secondary-600 font-bold">
                  ({params?.ddd}) *****{params?.phone}
                </Text>
              </Text>

              <CodeDigits
                onChange={(value) => setCode(value)}
                wrongCode={wrongCode}
              />

              <RenderLinkButton />

              <View className="border-t border-t-gray-100 mt-8">
                <Button
                  disabled={buttonLoading}
                  onPress={handleValidate}
                  className="mt-4"
                >
                  Continuar
                </Button>

                {/* <Text className="text-center uppercase mt-9 text-gray-400 text-sm">
              Você é nova por aqui?{' '}
              <TouchableOpacity
                onPress={() => navigate('MarisaServicos_CreateAccount', {})}
              >
                <Text className="font-medium text-primary-600">
                  Crie sua conta
                </Text>
              </TouchableOpacity>
            </Text> */}

                {error ? (
                  <View className="flex-row border rounded-lg border-feedback-error-main bg-feedback-error-bkg p-3 mt-8 items-center justify-between">
                    <View className="flex-row items-center">
                      <ErrorSVG />

                      <Text className="ml-2 text-feedback-error-text text-xs">
                        {error}
                      </Text>
                    </View>

                    <TouchableOpacity onPress={() => setError('')}>
                      <CloseErrorSVG />
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View className="mt-8 h-[42px]" />
                )}
              </View>
            </View>
          </View>
          <TouchableOpacity
            className="absolute top-5 right-5 z-10"
            onPress={goBack}
          >
            <CloseSVG height={24} width={24} />
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </>
  );
};

export default OTPValidation;
